import { Fragment } from "react";
import { Check, X, CircleDot } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { PACKAGES, COMPARISON } from "@/data/content";
import { cn } from "@/lib/utils";

const Value = ({ v }) => {
    if (v === "Not Included")
        return (
            <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-400">
                <X className="h-3.5 w-3.5 shrink-0" />Not Included
            </span>
        );
    if (v === "Included")
        return (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600">
                <Check className="h-3.5 w-3.5 shrink-0" />Included
            </span>
        );
    if (v === "On Request" || v === "Provision")
        return (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-flame-dark">
                <CircleDot className="h-3.5 w-3.5 shrink-0" />{v}
            </span>
        );
    return <span className="text-[12px] font-medium leading-snug text-slate-700">{v}</span>;
};

let rowIdx = -1;

export const PackageComparison = () => (
    <section id="comparison" data-testid="package-comparison-section" className="relative overflow-hidden bg-omsivo-paper py-24 lg:py-36">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
            <SectionHeading
                id="comparison-heading"
                overline="Package Comparison"
                title={<>Compare. <span className="text-flame-dark">Then decide with clarity.</span></>}
                description="What changes from one package to the next — structure, flooring, kitchen, bathroom and doors & windows, compared side by side with confirmed OMSIVO specifications."
            />

            <Reveal delay={0.15}>
                <div className="mt-12 overflow-x-auto rounded-2xl border border-omsivo-navy/10 bg-white shadow-xl shadow-omsivo-navy/5"
                    data-testid="comparison-table-wrap" data-lenis-prevent>
                    <table className="w-full min-w-[1020px] border-collapse text-left">
                        <thead>
                            <tr className="bg-omsivo-navy text-white">
                                <th className="sticky left-0 z-20 min-w-[170px] bg-omsivo-navy px-5 py-6 align-top font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                                    Specification
                                </th>
                                {PACKAGES.map((p, i) => (
                                    <th key={p.name} data-testid={`comparison-head-${p.tier}`}
                                        className={cn("min-w-[168px] px-5 py-6 align-top", i === 4 && "bg-white/[0.06]")}>
                                        <span className="block font-display text-[15px] font-black uppercase tracking-wide">{p.name}</span>
                                        <span className="mt-1.5 block font-display text-xl font-black tracking-tight text-flame">
                                            ₹{p.rate.toLocaleString("en-IN")}
                                            <span className="ml-1 font-mono text-[9px] font-medium uppercase tracking-widest text-white/50">/ Sq.Ft.</span>
                                        </span>
                                        <span className="mt-2 inline-block rounded-full border border-flame/40 bg-flame/10 px-2.5 py-1 font-mono text-[8px] font-semibold uppercase tracking-[0.16em] text-flame">
                                            {p.tag}
                                        </span>
                                        <span className="mt-2 block text-[10px] font-normal leading-snug text-white/55">
                                            <span className="font-semibold uppercase tracking-wider text-white/75">Best for: </span>
                                            {p.bestFor}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {COMPARISON.map((cat) => (
                                <Fragment key={cat.category}>
                                    <tr>
                                        <td colSpan={6} className="bg-omsivo-deep px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-flame"
                                            data-testid={`comparison-category-${cat.category.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                                            {cat.category}
                                        </td>
                                    </tr>
                                    {cat.rows.map((r) => {
                                        rowIdx += 1;
                                        const ri = rowIdx;
                                        return (
                                            <tr key={r.spec} className={cn(ri % 2 ? "bg-omsivo-paper/60" : "bg-white")} data-testid={`comparison-row-${ri}`}>
                                                <td className={cn("sticky left-0 z-10 min-w-[170px] px-5 py-3.5 font-display text-[13px] font-bold uppercase tracking-wide text-omsivo-navy shadow-[8px_0_12px_-8px_rgba(10,37,64,0.12)]",
                                                    ri % 2 ? "bg-[#f2f5f9]" : "bg-white")}>
                                                    {r.spec}
                                                </td>
                                                {r.values.map((v, vi) => (
                                                    <td key={vi} className={cn("px-5 py-3.5 align-top", vi === 4 && "bg-flame/[0.04]")}>
                                                        <Value v={v} />
                                                    </td>
                                                ))}
                                            </tr>
                                        );
                                    })}
                                </Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Reveal>

            <Reveal delay={0.2}>
                <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500" data-testid="comparison-legend">
                    <span className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> Included in package</span>
                    <span className="flex items-center gap-2"><X className="h-3.5 w-3.5 text-slate-400" /> Not included</span>
                    <span className="flex items-center gap-2"><CircleDot className="h-3.5 w-3.5 text-flame-dark" /> On request / provision</span>
                </div>
                <p className="mt-5 max-w-3xl text-xs leading-relaxed text-slate-500" data-testid="comparison-note">
                    Final scope and specifications are confirmed as per the selected package, project requirements and approved OMSIVO scope of work.
                </p>
                <p className="mt-2 max-w-3xl text-xs leading-relaxed text-slate-500">
                    Specifications may vary based on project design, site conditions and mutually agreed scope.
                </p>
            </Reveal>
        </div>
    </section>
);
